import { useEffect, useRef, useState } from 'react';
import { Upload, Loader2, Copy, Clock, CheckCircle2, XCircle, Hourglass, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useData } from '@/hooks/useData';
import { adService, AD_PACKAGES, PIX, formatBRL } from '@/services/ads';
import { formatDate } from '@/lib/format';
import { useSeo } from '@/lib/seo';
import { PageTitle } from './DashboardPages';
import { EmptyState } from '@/components/common/Primitives';
import { Countdown } from '@/components/common/Countdown';

const BANNER_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const PROOF_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];

const STATUS = {
  aguardando_pagamento: { text: 'Aguardando pagamento', cls: 'border-amber-300/25 bg-amber-300/10 text-amber-200', icon: Hourglass },
  em_analise: { text: 'Comprovante em análise', cls: 'border-cyan-300/25 bg-cyan-300/10 text-cyan-100', icon: Clock },
  agendada: { text: 'Na fila', cls: 'border-white/10 bg-white/5 text-foreground', icon: Clock },
  ativa: { text: 'No ar', cls: 'border-emerald-400/25 bg-emerald-400/10 text-emerald-200', icon: CheckCircle2 },
  expirada: { text: 'Encerrada', cls: 'border-white/10 bg-white/5 text-muted-foreground', icon: CheckCircle2 },
  recusada: { text: 'Recusada', cls: 'border-[rgba(255,59,92,0.3)] bg-[rgba(255,59,92,0.1)] text-[rgb(255,150,165)]', icon: XCircle },
};

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-white/10 bg-[#0b0f16] px-3 py-2">
      <div className="font-display text-lg font-semibold tabular-nums">{value}</div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
    </div>
  );
}

function CampaignRow({ c, busy, onProof }) {
  const info = STATUS[c.status] || STATUS.aguardando_pagamento;
  const Icon = info.icon;
  const [metrics, setMetrics] = useState(null);
  const pkg = AD_PACKAGES.find((p) => p.days === c.days);

  useEffect(() => {
    if (c.status === 'ativa' || c.status === 'expirada') {
      adService.metrics(c.id).then(setMetrics).catch(() => setMetrics(null));
    }
  }, [c.id, c.status]);

  return (
    <div className="surface p-4" data-testid="ad-campaign-row">
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <img src={c.image_url} alt="Banner da campanha" className="h-16 w-64 shrink-0 rounded-md border border-white/10 object-cover" />
        <div className="min-w-0 flex-1">
          <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs ${info.cls}`}>
            <Icon className="h-3 w-3" /> {info.text}
          </span>
          <div className="mt-1 text-sm">{pkg?.label || `${c.days} dias`} · {formatBRL(c.price_cents)}</div>
          {c.starts_at && (
            <div className="text-[11px] text-muted-foreground">
              {c.status === 'agendada' ? 'Começa em' : 'Período'} {formatDate(c.starts_at)} até {formatDate(c.ends_at)}
            </div>
          )}
          <div className="mt-2"><Countdown c={c} /></div>
          {c.admin_note && <div className="mt-1 text-xs text-[rgb(255,150,165)]">Motivo: {c.admin_note}</div>}
        </div>
        {c.status === 'aguardando_pagamento' && (
          <Button size="sm" className="btn-glow" disabled={busy} onClick={() => onProof(c)} data-testid="ad-send-proof">
            <Upload /> Enviar comprovante
          </Button>
        )}
      </div>
      {metrics && (
        <div className="mt-3 grid grid-cols-3 gap-2" data-testid="ad-metrics">
          <Stat label="Visualizações" value={metrics.views} />
          <Stat label="Cliques" value={metrics.clicks} />
          <Stat label="Taxa de clique (CTR)" value={`${metrics.ctr}%`} />
        </div>
      )}
    </div>
  );
}

export function AdvertisePage() {
  useSeo({ title: 'Anunciar na capa' });
  const { myCompany, authLoading } = useData();
  const [days, setDays] = useState(3);
  const [banner, setBanner] = useState('');
  const [link, setLink] = useState('');
  const [campaigns, setCampaigns] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [proofTarget, setProofTarget] = useState(null);
  const bannerRef = useRef(null);
  const proofRef = useRef(null);
  const pkg = AD_PACKAGES.find((p) => p.days === days);

  const load = async () => {
    if (!myCompany?.id) return;
    try {
      setCampaigns(await adService.mine(myCompany.id));
    } catch (e) {
      toast.error(e.message);
      setCampaigns([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myCompany?.id]);

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(PIX.key);
      toast.success('Chave Pix copiada');
    } catch (e) {
      toast.error('Não foi possível copiar. Copie a chave manualmente.');
    }
  };

  const onBannerChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!BANNER_TYPES.includes(file.type)) return toast.error('Use uma imagem PNG, JPG ou WebP.');
    if (file.size > 3 * 1024 * 1024) return toast.error('O banner precisa ter até 3 MB.');
    setUploading(true);
    try {
      setBanner(await adService.uploadBanner(file));
      toast.success('Banner pronto');
    } catch (ex) {
      toast.error(ex.message);
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!banner) return toast.error('Envie o banner antes de continuar.');
    if (!/^https:\/\/\S+$/.test(link.trim())) return toast.error('O link precisa começar com https://');
    setBusy(true);
    try {
      await adService.request(days, banner, link.trim());
      toast.success('Pedido criado', { description: `Faça o Pix de ${formatBRL(pkg.priceCents)} e envie o comprovante na lista abaixo.` });
      setBanner('');
      setLink('');
      await load();
    } catch (ex) {
      toast.error(ex.message);
    } finally {
      setBusy(false);
    }
  };

  const onProof = (c) => {
    setProofTarget(c);
    proofRef.current?.click();
  };

  const onProofChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !proofTarget) return;
    if (!PROOF_TYPES.includes(file.type)) return toast.error('Envie uma imagem (JPG, PNG ou WebP) ou um PDF.');
    if (file.size > 5 * 1024 * 1024) return toast.error('O comprovante precisa ter até 5 MB.');
    setBusy(true);
    try {
      const path = await adService.uploadProof(file);
      await adService.submitProof(proofTarget.id, path);
      toast.success('Comprovante enviado', { description: 'A equipe vai analisar e liberar sua campanha.' });
      setProofTarget(null);
      await load();
    } catch (ex) {
      toast.error(ex.message);
    } finally {
      setBusy(false);
    }
  };

  if (authLoading) return <div className="text-sm text-muted-foreground">Carregando…</div>;

  if (!myCompany) {
    return (
      <EmptyState
        icon={Megaphone}
        title="Cadastre sua empresa"
        text="Para anunciar na capa, sua empresa precisa estar cadastrada e ativa."
        testId="ads-no-company"
      />
    );
  }

  return (
    <div className="max-w-4xl space-y-6" data-testid="advertise-page">
      <PageTitle
        title="Anunciar na capa"
        text="Seu banner aparece no carrossel da capa dos perfis, junto com outras empresas. Até 10 anúncios rodam ao mesmo tempo."
      />

      <section className="surface space-y-4 p-5">
        <h2 className="font-display text-base font-semibold">1. Escolha o período</h2>
        <div className="grid gap-2 sm:grid-cols-3">
          {AD_PACKAGES.map((p) => (
            <button
              key={p.days}
              type="button"
              onClick={() => setDays(p.days)}
              className={`rounded-lg border px-4 py-3 text-left transition-colors ${days === p.days ? 'border-cyan-300/40 bg-cyan-300/10' : 'border-white/10 bg-[#0b0f16] hover:border-white/20'}`}
              data-testid={`ad-package-${p.days}`}
            >
              <div className="font-medium">{p.label}</div>
              <div className="font-display text-xl font-semibold">{formatBRL(p.priceCents)}</div>
              <div className="text-[11px] text-muted-foreground">{formatBRL(Math.round(p.priceCents / p.days))} por dia</div>
            </button>
          ))}
        </div>
      </section>

      <section className="surface space-y-4 p-5">
        <h2 className="font-display text-base font-semibold">2. Envie o banner</h2>
        <p className="text-xs text-muted-foreground">
          Formato 1600 × 400 px (proporção 4:1), JPG, PNG ou WebP, até 3 MB. A imagem é ajustada automaticamente. Deixe logo e texto importantes no centro.
        </p>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          {banner ? (
            <img src={banner} alt="Prévia do banner" className="h-24 w-full max-w-md rounded-md border border-white/10 object-cover" />
          ) : (
            <div className="grid h-24 w-full max-w-md place-items-center rounded-md border border-dashed border-white/15 text-xs text-muted-foreground">
              Nenhum banner enviado
            </div>
          )}
          <Button type="button" variant="secondary" className="border border-white/10" disabled={uploading} onClick={() => bannerRef.current?.click()} data-testid="ad-banner-button">
            {uploading ? <Loader2 className="animate-spin" /> : <Upload />} {banner ? 'Trocar banner' : 'Escolher banner'}
          </Button>
          <input ref={bannerRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onBannerChosen} />
        </div>
      </section>

      <section className="surface space-y-4 p-5">
        <h2 className="font-display text-base font-semibold">3. Link de destino</h2>
        <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://www.suaempresa.com.br" data-testid="ad-link-input" />
        <p className="text-[11px] text-muted-foreground">Quando alguém clicar no banner, será levado a este endereço. Precisa começar com https://.</p>
      </section>

      <section className="surface space-y-4 p-5">
        <h2 className="font-display text-base font-semibold">4. Pague por Pix</h2>
        <div className="space-y-1 rounded-md border border-white/10 bg-[#0b0f16] p-4 text-sm">
          <div>Valor: <span className="font-semibold">{formatBRL(pkg.priceCents)}</span> ({pkg.label})</div>
          <div>Chave Pix: <span className="font-mono">{PIX.key}</span></div>
          <div>Nome: {PIX.name}</div>
          <Button size="sm" variant="secondary" className="mt-2 border border-white/10" onClick={copyKey}>
            <Copy /> Copiar chave
          </Button>
        </div>
        <Button className="btn-glow" disabled={busy || uploading} onClick={submit} data-testid="ad-request-button">
          {busy ? <Loader2 className="animate-spin" /> : <CheckCircle2 />} Criar pedido de anúncio
        </Button>
        <p className="text-[11px] text-muted-foreground">
          Depois de criar o pedido, faça o Pix e envie o comprovante na lista abaixo. A campanha só entra no ar depois da aprovação.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="font-display text-base font-semibold">Minhas campanhas</h2>
        {campaigns === null ? (
          <div className="text-sm text-muted-foreground">Carregando…</div>
        ) : campaigns.length ? (
          campaigns.map((c) => <CampaignRow key={c.id} c={c} busy={busy} onProof={onProof} />)
        ) : (
          <EmptyState icon={Megaphone} title="Nenhuma campanha ainda" text="Crie seu primeiro pedido acima." testId="ads-empty" />
        )}
      </section>

      <input
        ref={proofRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,application/pdf"
        className="hidden"
        onChange={onProofChosen}
      />
    </div>
  );
}